import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Pill, Plus, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export default function MedListDialog({ open, onOpenChange, onChanged }) {
  const [customMeds, setCustomMeds] = useState([]);
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/med-list/custom");
      setCustomMeds(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const add = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.post("/med-list", { name: newName });
      toast.success(res.data.message);
      setNewName("");
      load();
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (name) => {
    try {
      const res = await api.delete(`/med-list/${encodeURIComponent(name)}`);
      toast.success(res.data.message);
      load();
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="med-list-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-[#0F2547] flex items-center gap-2">
            <Pill className="h-5 w-5 text-[#00A499]" /> Medication List
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-500">
            Add your own medications to the dropdown. Standard items can't be removed.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={add} className="flex gap-2 mb-4" data-testid="add-med-form">
          <input required placeholder="Medication name *" value={newName}
            onChange={(e) => setNewName(e.target.value)}
            data-testid="add-med-input"
            className="flex-1 h-12 rounded-lg border border-slate-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#005EB8]" />
          <button type="submit" disabled={saving} data-testid="add-med-submit"
            className="h-12 px-4 rounded-lg bg-[#005EB8] hover:bg-[#004a93] transition-colors text-white text-sm font-semibold disabled:opacity-60 inline-flex items-center gap-2">
            <Plus className="h-4 w-4" /> Add
          </button>
        </form>

        <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Your custom medications</p>
        {customMeds.length === 0 ? (
          <p className="text-sm text-slate-500 py-2" data-testid="no-custom-meds">None yet — add one above.</p>
        ) : (
          <div className="flex flex-wrap gap-2" data-testid="custom-med-list">
            {customMeds.map((m) => (
              <span key={m} className="inline-flex items-center gap-1.5 bg-teal-50 border border-teal-200 text-teal-900 text-xs font-bold px-3 py-1.5 rounded-full">
                {m}
                <button onClick={() => remove(m)} data-testid={`remove-med-${m.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="text-teal-400 hover:text-[#D5281B] min-w-[24px] min-h-[24px] grid place-items-center" title={`Remove ${m}`}>
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
